import React from "react";
import "./Terms.css";
import Container from "react-bootstrap/Container";
import { GUARANTEE_DAYS } from "../components/course/courseContent";

export default function Terms() {
  return (
    <main className="terms-page">
      <Container>
        <h1 className="page-title d-flex justify-content-center flex-nowrap">
          Terms of Service
        </h1>

        <div className="page-content">
          <p>
            <strong>Last Updated:</strong> August 8, 2026
          </p>

          <p>
            <strong>IMPORTANT – READ CAREFULLY.</strong> These Terms of Service
            (“<strong>Terms</strong>”) are a binding agreement between you and
            Lumiwealth, Inc., a Delaware corporation (“
            <strong>Lumiwealth</strong>,” “<strong>we</strong>,” “
            <strong>us</strong>,” or “<strong>our</strong>”). By accessing or
            using our websites (including{" "}
            <a
              href="https://botspot.trade"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://botspot.trade
            </a>{" "}
            and{" "}
            <a
              href="https://lumiwealth.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://lumiwealth.com
            </a>
            ), software, APIs, tools, algorithm marketplace, or related services
            (collectively, the “<strong>Services</strong>”), you agree to these
            Terms.
          </p>

          <p>
            <em>
              We provide software tools and connectivity. We do not provide
              investment, legal, tax, or accounting advice; we are not a
              broker-dealer, not an investment adviser, and do not act as your
              agent or fiduciary. You are solely responsible for your trading
              decisions and for complying with applicable laws and
              broker/exchange requirements.
            </em>
          </p>

          <h2>1) Definitions</h2>
          <p>
            <strong>“Account”</strong> means your user account for the Services.
          </p>
          <p>
            <strong>“Broker”</strong> means your third-party brokerage,
            exchange, or custodian through which you trade.
          </p>
          <p>
            <strong>“Marketplace”</strong> means Lumiwealth’s platform allowing
            third parties to list, distribute, or license algorithms, code,
            strategies, models, signals, or content.
          </p>
          <p>
            <strong>“User Code”</strong> means any code, models, prompts,
            configurations, or data you provide or run through the Services.
          </p>
          <p>
            <strong>“Seller”</strong> means a third party listing content or
            code in the Marketplace.
          </p>

          <h2>2) Who We Are (and What We Are Not)</h2>
          <p>
            Lumiwealth provides tools to build, test, and connect automated
            trading strategies to third-party Brokers you select. We do not open
            brokerage accounts, hold customer funds or securities, route orders,
            or provide personalized investment advice. Nothing in the Services
            is an offer, solicitation, recommendation, or endorsement of any
            security, instrument, or strategy.
          </p>

          <h2>3) Eligibility; Compliance</h2>
          <p>
            You must be at least 18 and legally capable of entering a contract.
            You represent you are not subject to U.S. sanctions or export
            restrictions and that you will use the Services in compliance with
            applicable law. We may deny or restrict access to meet legal,
            regulatory, risk, or business requirements.
          </p>

          <h2>4) Service Reliability and Changes</h2>
          <p>
            The Services depend on software, networks, market data, Brokers,
            exchanges, and other third parties. Outages, delays, errors,
            interrupted connectivity, and changes to features may occur. We do
            not guarantee uninterrupted or error-free operation, any particular
            availability or response time, or that every feature will remain
            available. You are responsible for monitoring your strategies,
            orders, positions, and accounts and for maintaining safeguards
            appropriate to your use, especially before enabling live trading.
          </p>

          <h2>5) License; Acceptable Use</h2>
          <p>
            Subject to these Terms, we grant you a limited, revocable,
            non‑exclusive, non‑transferable license to access and use the
            Services for your own lawful trading and research. You will not (and
            will not permit others to): (a) copy, modify, reverse engineer, or
            create derivative works of the Services; (b) bypass security; (c)
            interfere with or overload the Services; (d) misuse market data or
            violate exchange/broker terms; (e) engage in illegal, manipulative,
            or abusive trading; or (f) use the Services to compete with us or to
            train models that replicate our offerings.
          </p>

          <h2>6) Your Broker, Market Data, and Other Third Parties</h2>
          <p>
            You authorize the Services to transmit instructions to your Broker
            on your behalf when you enable connectivity.{" "}
            <strong>Orders can fail</strong> or behave unexpectedly due to
            Broker/exchange halts, margin/collateral limits, risk checks,
            throttling, stale data, connectivity loss, or third‑party outages.{" "}
            <strong>You are solely responsible</strong> for monitoring
            positions, orders, and account balances with your Broker. Market
            data and third‑party services are provided “as available,” may be
            delayed or inaccurate, and are subject to the provider’s terms; you
            will comply with any non‑professional, redistribution, display, or
            usage restrictions.
          </p>

          <h2>7) Marketplace & AI‑Generated Content</h2>
          <p>
            The Marketplace may host algorithms, code, models, prompts, and
            content from Sellers or generated via AI.{" "}
            <strong>
              All Marketplace items and AI outputs are provided “as is,” with no
              warranties of any kind.
            </strong>{" "}
            You are responsible for vetting, testing (including
            paper/backtesting), security scanning, and auditing any code before
            use; running third‑party or AI code in live trading is solely at
            your risk.
          </p>
          <p>
            <strong>Sellers’ Representations.</strong> Sellers represent they
            own or have rights to list their content, that it does not include
            malicious code, and that listing does not violate third‑party rights
            or law.
          </p>
          <p>
            <strong>Licenses.</strong> Sellers grant buyers the license
            specified on the listing; Sellers grant Lumiwealth a license to
            host, test, scan (including for security/malware), and display their
            listings and related metadata.
          </p>
          <p>
            <strong>Indemnity (Sellers & Users).</strong> Sellers and Users will
            defend, indemnify, and hold harmless Lumiwealth and its affiliates
            from claims, losses, liabilities, and costs (including attorneys’
            fees) arising from their code, content, conduct, or violations of
            law or third‑party rights.
          </p>
          <p>
            We may remove or disable Marketplace items suspected to be insecure,
            infringing, or non‑compliant.
          </p>

          <h2>8) Performance, Backtesting & Paper vs. Live</h2>
          <p>
            Backtests, simulations, paper trading, and hypothetical performance
            have material limitations and{" "}
            <strong>are not predictive of future results</strong>. You agree not
            to rely on such results for live trading decisions and acknowledge
            that live results often differ materially due to latency, slippage,
            liquidity, fees, partial fills, and market impact. Where we display
            hypothetical performance, we will use cautionary statements and
            placement consistent with{" "}
            <a
              href="https://www.law.cornell.edu/cfr/text/17/4.41"
              target="_blank"
              rel="noopener noreferrer"
            >
              17 CFR §4.41
            </a>{" "}
            and related NFA guidance, and you agree such information is for
            illustrative/educational purposes only.
          </p>

          <h2>9) Multi‑Asset Risk Disclosures (You Must Read This)</h2>
          <p>
            <strong>General.</strong> Trading involves substantial risk and may
            result in the loss of some or all capital. Leveraged products can
            result in losses exceeding your initial deposit.
          </p>
          <p>
            <strong>Options.</strong> Options are not suitable for all
            investors. Before trading options, you must read the OCC’s{" "}
            <em>Characteristics and Risks of Standardized Options (ODD)</em> and
            acknowledge receipt. See the latest ODD and guidance from the
            Options Clearing Corporation/FINRA:{" "}
            <a
              href="https://www.theocc.com/company-information/documents-and-archives/options-disclosure-document"
              target="_blank"
              rel="noopener noreferrer"
            >
              OCC ODD
            </a>
            .{" "}
          </p>
          <p>
            <strong>Futures / Security Futures.</strong> There is a high degree
            of risk in futures trading. Review risk disclosures from your FCM
            and the NFA/CFTC and ensure suitability before trading.
          </p>
          <p>
            <strong>Pattern Day Trading.</strong> If you day trade equities, you
            are responsible for complying with FINRA’s PDT rules (including
            minimum equity requirements) and your Broker’s policies.
          </p>

          <h2>10) No Advice; No Fiduciary Duty</h2>
          <p>
            We do not provide personalized investment advice, recommendations,
            or fiduciary services. Any information, examples, or performance
            displays are educational, impersonal, and general. You are solely
            responsible for your trading decisions and for consulting your own
            financial, legal, tax, and accounting advisors.
          </p>

          <h2>11) Security; Your Responsibilities</h2>
          <p>
            You must protect your credentials, API keys, secrets, and devices,
            enable multi‑factor authentication where offered, and promptly
            notify us of suspected compromise. We implement commercially
            reasonable security measures but do not guarantee error‑free or
            breach‑proof security. If we learn of a breach affecting your
            information, we will notify you as required by applicable law.
          </p>

          <h2>12) Fees, Billing, Trials & Refunds</h2>
          <p>
            We may charge subscription, usage, transaction, or Marketplace fees
            as posted at checkout or in your account. Fees are billed in advance
            (unless stated otherwise) and are non‑refundable except as required
            by law or expressly stated in our refund policy. Taxes and
            third‑party fees (e.g., Broker commissions, exchange/market data
            fees) are your responsibility. You may cancel in account settings;
            access continues until the end of the current billing period unless
            we specify otherwise.
          </p>
          <p>
            <strong>Live AI Trading Bootcamp guarantee.</strong> A purchase of
            the live October 2026 AI Trading Bootcamp is covered by a {GUARANTEE_DAYS}-day
            money-back guarantee measured from the cohort's first scheduled
            live session. You may request a refund by emailing
            support@lumiwealth.com from the email address used for the purchase
            before that window ends. We will refund Bootcamp installments paid
            through the request date and cancel any remaining Bootcamp
            installments. This guarantee does not limit rights provided by
            applicable law.
          </p>

          <h2>13) Intellectual Property</h2>
          <p>
            The Services, including all software, UI, branding, and content, are
            owned by us or our licensors. Except for the limited license above,
            no rights are granted. You may provide feedback, and we may use it
            without restriction or compensation.
          </p>

          <h2>14) Third‑Party Terms; Pass‑Through</h2>
          <p>
            Use of Brokers, exchanges, market data, cloud providers, payment
            processors (e.g., Stripe), or other third‑party services is subject
            to their terms and fees. By connecting or using them through the
            Services, you agree to their terms and acknowledge we are not
            responsible for their acts or omissions.
          </p>

          <h2>15) Warranty Disclaimer</h2>
          <p>
            <strong>
              THE SERVICES (INCLUDING ANY ALGORITHMS, MARKETPLACE ITEMS, AI
              OUTPUTS, DATA, AND INTEGRATIONS) ARE PROVIDED “AS IS” AND “AS
              AVAILABLE.”
            </strong>{" "}
            TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
            EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, NON‑INFRINGEMENT, AVAILABILITY, ACCURACY, AND
            ERROR‑FREE OPERATION.
          </p>

          <h2>16) Limitation of Liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, LUMIWEALTH WILL NOT BE
            LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY,
            OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST TRADING
            OPPORTUNITIES, LOST DATA, BUSINESS INTERRUPTION, OR COSTS OF
            SUBSTITUTE GOODS/SERVICES, EVEN IF ADVISED OF THE POSSIBILITY. OUR
            TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE
            SERVICES WILL NOT EXCEED{" "}
            <strong>
              THE AMOUNT YOU PAID TO LUMIWEALTH FOR THE SERVICES IN THE 12
              MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY
            </strong>{" "}
            OR USD $100, WHICHEVER IS GREATER.
          </p>

          <h2>17) Indemnification</h2>
          <p>
            You will defend, indemnify, and hold harmless Lumiwealth and its
            affiliates, officers, directors, employees, and agents from and
            against claims, damages, liabilities, costs, and expenses (including
            reasonable attorneys’ fees) arising from: (a) your use of the
            Services; (b) your User Code or Marketplace activity; (c) your
            violation of law or third‑party rights; or (d) your breach of these
            Terms.
          </p>

          <h2>18) Term; Suspension; Termination</h2>
          <p>
            We may suspend or terminate access immediately for suspected misuse,
            security risk, non‑payment, legal/regulatory risk, or violations of
            these Terms. Upon termination, your license ends and you must cease
            using the Services; Sections that by nature should survive (e.g.,
            IP, Marketplace terms, fees due, disclaimers, limitations,
            indemnities, dispute resolution) survive.
          </p>

          <h2>19) Dispute Resolution; Arbitration; Class Action Waiver</h2>
          <p>
            <strong>Binding Arbitration.</strong> You and Lumiwealth agree to
            resolve any dispute arising out of or relating to these Terms or the
            Services by binding arbitration administered by AAA (or JAMS) under
            its applicable rules. The seat will be Delaware.{" "}
            <strong>Class Action Waiver:</strong> Disputes are conducted only on
            an individual basis; class, consolidated, or representative actions
            are not permitted. <strong>30‑Day Opt‑Out:</strong> You may opt out
            of arbitration/class waiver by sending written notice to
            legal@lumiwealth.com within 30 days of first accepting these Terms.
          </p>

          <h2>20) Compliance, Sanctions & Export Controls</h2>
          <p>
            You represent you are not located in or ordinarily resident in any
            country or region subject to comprehensive U.S. sanctions and are
            not on any U.S. restricted list. You will not use the Services for
            prohibited purposes and will cooperate with reasonable KYC/AML
            requests where applicable.
          </p>

          <h2>21) Changes to the Services and Terms</h2>
          <p>
            We may modify the Services or these Terms at any time. Material
            changes will be posted on our site or in‑product. Your continued use
            after changes become effective constitutes acceptance.
          </p>

          <h2>22) Miscellaneous</h2>
          <p>
            <strong>Governing Law.</strong> Delaware law, without regard to
            conflicts rules. <strong>Force Majeure.</strong> We are not liable
            for delays or failures due to events beyond our reasonable control.{" "}
            <strong>Assignment.</strong> You may not assign without our consent;
            we may assign. <strong>Severability; Waiver.</strong> If a term is
            unenforceable, the remainder remains in force; no waiver is
            effective unless in writing. <strong>Entire Agreement.</strong>{" "}
            These Terms plus policies referenced herein (including our Privacy
            Policy) are the entire agreement.
          </p>

          <h2>23) Contact</h2>
          <p>
            Questions? Email{" "}
            <a href="mailto:support@lumiwealth.com">support@lumiwealth.com</a>.
          </p>

          <hr />
          <p>
            <em>
              Regulatory references (informational): OCC ODD (options), NFA/CFTC
              risk disclosures (futures), and FINRA’s PDT rules are industry
              standards that you must review and comply with via your Broker and
              applicable law.
            </em>
          </p>
        </div>
      </Container>
    </main>
  );
}
